import { getPrismicLocale, Locales } from "@/lib/i18n";
import { createClient } from "@/prismicio";
import { CategorieDocument } from "@/prismicio-types";

export interface CategorieDocumentWithChildren extends CategorieDocument {
  children: CategorieDocumentWithChildren[];
}

export const resolveCategoriesTree = async (lang: Locales): Promise<CategorieDocumentWithChildren[]> => {
  const client = createClient();

  try {
    const categories = await client.getAllByType("categorie", {
      fetchLinks: ["categorie.name"],
      lang: getPrismicLocale(lang),
    });

    if (!categories) {
      return [];
    }

    return buildCategoryTree(categories);
  } catch (error) {
    console.error("Error fetching categories:", error);
    return [];
  }
};

/**
 * Sorts categories by their display property in ascending order.
 * Categories with undefined display values are placed at the end.
 *
 * @param categories - Array of categories to sort
 * @returns Sorted array of categories
 */
const sortCategoriesByDisplay = (categories: CategorieDocumentWithChildren[]): CategorieDocumentWithChildren[] => {
  return categories.sort((a, b) => {
    const displayA = a.data.display;
    const displayB = b.data.display;

    // If both are undefined, maintain original order
    if (displayA == null && displayB == null) {
      return 0;
    }

    // If only a is undefined, put it after b
    if (displayA == null) {
      return 1;
    }

    // If only b is undefined, put it after a
    if (displayB == null) {
      return -1;
    }

    // Both have values, sort in ascending order
    if (displayA < displayB) {
      return -1;
    }
    if (displayA > displayB) {
      return 1;
    }
    return 0;
  });
};

/**
 * Builds a hierarchical tree structure from a flat array of category objects.
 *
 * Each category may reference a parent category by ID. This function organizes
 * the categories into a tree, where each category contains a `children` array
 * of its direct descendants. Categories without a parent are considered root nodes.
 *
 * Categories are sorted by their display property in ascending order at each level,
 * with undefined display values placed at the end.
 *
 * @param categories - An array of category objects. Each object must have an `id` property
 *   and a `data.parentCategorie` property, where `parentCategorie` may be an object with an `id`
 *   or `null`/`undefined` for root categories.
 * @returns An array of root category objects, each with a `children` property containing its descendants.
 */

export const buildCategoryTree = (categories: CategorieDocument[]): CategorieDocumentWithChildren[] => {
  // Create a map for easy access to categories by ID
  const categoryMap: Record<string, CategorieDocumentWithChildren> = {};
  categories.forEach((category) => {
    categoryMap[category.id] = {
      ...category,
      children: [],
    };
  });

  // Structure to store the final tree
  const rootCategories: CategorieDocumentWithChildren[] = [];

  // Organize categories into the tree
  categories.forEach((category) => {
    const categoryWithChildren = categoryMap[category.id];

    // Check if the category has a parent
    const parentId = "id" in category.data.parentCategorie && category.data.parentCategorie?.id;

    if (parentId) {
      // Add the category as a child of its parent if the parent exists
      if (categoryMap[parentId]) {
        categoryMap[parentId].children.push(categoryWithChildren);
      } else {
        // If the parent does not exist in our list, treat as root
        rootCategories.push(categoryWithChildren);
      }
    } else {
      // Root category (no parent)
      rootCategories.push(categoryWithChildren);
    }
  });

  // Sort all categories at each level by display property
  const sortTree = (categories: CategorieDocumentWithChildren[]): CategorieDocumentWithChildren[] => {
    const sorted = sortCategoriesByDisplay(categories);

    // Recursively sort children of each category
    sorted.forEach((category) => {
      if (category.children.length > 0) {
        category.children = sortTree(category.children);
      }
    });

    return sorted;
  };

  return sortTree(rootCategories);
};

/**
 * Find a category in the tree by its ID
 * @param tree - The category tree built with buildCategoryTree
 * @param categoryId - The ID of the category to find
 * @returns The found category or null if not found
 */
export const findCategoryInTree = (
  tree: CategorieDocumentWithChildren[],
  categoryId: string
): CategorieDocumentWithChildren | null => {
  for (const category of tree) {
    // Vérifier si c'est la catégorie recherchée
    if (category.id === categoryId) {
      return category;
    }

    // Rechercher récursivement dans les enfants
    if (category.children && category.children.length > 0) {
      const found = findCategoryInTree(category.children, categoryId);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * Find multiple categories in the tree by their IDs
 * @param tree - The category tree
 * @param categoryIds - Array of category IDs to find
 * @returns Array of found categories
 */
export const findCategoriesInTree = (
  tree: CategorieDocumentWithChildren[],
  categoryIds: string[]
): CategorieDocumentWithChildren[] => {
  const foundCategories: CategorieDocumentWithChildren[] = [];

  categoryIds.forEach((id) => {
    const category = findCategoryInTree(tree, id);
    if (category) {
      foundCategories.push(category);
    }
  });

  return foundCategories;
};

/**
 * Find a category and return its full hierarchical path
 * @param tree - The category tree
 * @param categoryId - The ID of the category
 * @returns The hierarchical path or null if not found
 */
export const findCategoryPathInTree = (
  tree: CategorieDocumentWithChildren[],
  categoryId: string,
  currentPath: string[] = []
): string[] | null => {
  for (const category of tree) {
    const newPath = [...currentPath, category.data.title ?? ""];

    // Vérifier si c'est la catégorie recherchée
    if (category.id === categoryId) {
      return newPath;
    }

    // Rechercher récursivement dans les enfants
    if (category.children && category.children.length > 0) {
      const found = findCategoryPathInTree(category.children, categoryId, newPath);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * Generate an object of type {lvl0: "Parent", lvl1: "Parent > Enfant", ...}
 * @param tree - The category tree
 * @param categoryId - The ID of the category
 * @returns Object with hierarchical levels or null if not found
 */
export const getCategoryHierarchyLevels = (
  tree: CategorieDocumentWithChildren[],
  categoryId: string
): Record<string, string> | null => {
  const path = findCategoryPathInTree(tree, categoryId);

  if (!path || path.length === 0) {
    return null;
  }

  const hierarchyLevels: Record<string, string> = {};

  // Construire chaque niveau
  for (let i = 0; i < path.length; i++) {
    const levelPath = path.slice(0, i + 1).join(" > ");
    hierarchyLevels[`lvl${i}`] = levelPath;
  }

  return hierarchyLevels;
};

/**
 * Useful for a product with multiple categories
 * @param tree - The category tree
 * @param productCategories - The categories related to the product
 * @returns Array of objects with hierarchical levels
 */
export const getProductCategoryHierarchyLevels = (
  tree: CategorieDocumentWithChildren[],
  productCategories: Array<{ category: { id: string } }>
): Array<Record<string, string>> => {
  const hierarchies: Array<Record<string, string>> = [];

  productCategories.forEach((productCategory) => {
    const hierarchy = getCategoryHierarchyLevels(tree, productCategory.category.id);
    if (hierarchy) {
      hierarchies.push(hierarchy);
    }
  });

  return hierarchies;
};
